import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Paintbrush, Bath, Hammer, Layers, Grid3x3, Wrench, Ruler, MoreHorizontal,
  Upload, X, Loader2, ArrowRight, ArrowLeft, CheckCircle2, Zap, Clock, CalendarDays, CalendarClock, MapPin,
} from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { WhatsAppButton } from "./CTAs";
import { ROME_ZONES } from "../../data";
import { WHATSAPP_ENABLED } from "../../config";
import { getAttribution } from "../../lib/attribution";
import { track } from "../../lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 10;
const MAX_SIZE = 4 * 1024 * 1024;

const WORKS = [
  { v: "Imbiancatura", icon: Paintbrush },
  { v: "Rifacimento bagno", icon: Bath },
  { v: "Piccola muratura", icon: Hammer },
  { v: "Cartongesso", icon: Layers },
  { v: "Piastrellatura", icon: Grid3x3 },
  { v: "Riparazione muro", icon: Wrench },
  { v: "Intonaco", icon: Ruler },
  { v: "Altro", icon: MoreHorizontal },
];
const SIZES = [
  { v: "Piccolo", d: "Un intervento circoscritto" },
  { v: "Medio", d: "Una o più stanze" },
  { v: "Grande", d: "Tutta la casa o più ambienti" },
];
const URGENCY = [
  { v: "Oggi", icon: Zap },
  { v: "Entro 2 giorni", icon: Clock },
  { v: "Entro una settimana", icon: CalendarDays },
  { v: "Nessuna urgenza", icon: CalendarClock },
];

const TOTAL = 7;

const OptionCard = ({ active, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`group text-left rounded-2xl border p-4 sm:p-5 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
      active ? "border-brand-orange bg-brand-orange/5 ring-2 ring-brand-orange/30" : "border-gray-200 bg-white hover:border-navy/30"
    }`}
  >
    {children}
  </button>
);

const fieldClass =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink/40 focus:border-navy focus:ring-2 focus:ring-navy/10 outline-none transition-colors";

export default function QuoteWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    tipo_lavoro: "", dimensione: "", urgenza: "", zona: "", zona_altro: "", descrizione: "",
    nome: "", telefono: "", email: "",
  });
  const [errors, setErrors] = useState({});
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const inputRef = useRef(null);
  const started = useRef(false);

  const set = (k, v) => {
    if (!started.current) { started.current = true; track.formStart(); }
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: "" } : e));
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList).slice(0, MAX_FILES - photos.length);
    let added = 0;
    files.forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > MAX_SIZE) { toast.error(`"${file.name}" supera i 4MB`); return; }
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, { name: file.name, data_url: reader.result }]);
      reader.readAsDataURL(file);
      added += 1;
    });
    if (added) track.photoUpload(added);
  };

  const canNext = () => {
    if (step === 1) return !!form.tipo_lavoro;
    if (step === 2) return !!form.dimensione;
    if (step === 3) return !!form.urgenza;
    if (step === 4) return !!form.zona && (form.zona !== "Altra zona" || !!form.zona_altro.trim());
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error(step === 4 ? "Seleziona la zona di Roma per continuare." : "Seleziona un'opzione per continuare.");
      return;
    }
    track.formStep(step);
    setStep((s) => Math.min(s + 1, TOTAL));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    const errs = {};
    const phoneDigits = form.telefono.replace(/\D/g, "");
    if (!form.telefono.trim()) errs.telefono = "Inserisci un numero di telefono o WhatsApp.";
    else if (phoneDigits.length < 8 || phoneDigits.length > 15) errs.telefono = "Inserisci un numero di telefono valido.";
    if (!form.email.trim()) errs.email = "Inserisci la tua email.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Inserisci un'email valida.";
    if (!privacyConsent) errs.privacy = "Devi accettare la Privacy Policy per continuare.";
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Controlla i campi obbligatori."); return; }
    setLoading(true);
    try {
      const attribution = getAttribution();
      const { provenienza, ...attr } = attribution;
      const finalZona = form.zona === "Altra zona" && form.zona_altro.trim() ? form.zona_altro.trim() : form.zona;
      const { zona_altro, ...rest } = form;
      const res = await axios.post(`${API}/leads`, { ...rest, zona: finalZona, photos, provenienza: provenienza || "Diretto", privacy_consent: privacyConsent, ...attr });
      track.formSubmit({ tipo_lavoro: form.tipo_lavoro, priorita: res.data.priorita, value: res.data.lead_score });
      navigate("/grazie", { state: { nome: form.nome || "", priorita: res.data.priorita, tipo: form.tipo_lavoro } });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (WHATSAPP_ENABLED ? "Errore nell'invio. Riprova o scrivici su WhatsApp." : "Errore nell'invio. Riprova più tardi o contattaci via email."));
      setLoading(false);
    }
  };

  const progress = Math.round((step / TOTAL) * 100);

  return (
    <section id="preventivo" className="relative bg-surface py-20 sm:py-28">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="text-center">
          <Reveal><Overline>Preventivo guidato</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-4xl sm:text-5xl">
              Il tuo preventivo in 7 passi
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-ink/65 text-base sm:text-lg">Meno di un minuto. Gratuito e senza impegno.</p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-10">
          <div className="rounded-3xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
            {/* progress */}
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-semibold text-navy">Passo {step} di {TOTAL}</span>
              <span className="text-ink/50">{progress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden mb-7">
              <motion.div className="h-full bg-brand-orange" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
              >
                {step === 1 && (
                  <div>
                    <h3 className="font-display font-bold text-xl text-navy mb-5">Che lavoro devi eseguire?</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {WORKS.map((w) => {
                        const Icon = w.icon;
                        return (
                          <OptionCard key={w.v} active={form.tipo_lavoro === w.v} onClick={() => set("tipo_lavoro", w.v)} testid={`wiz-work-${w.v}`}>
                            <Icon size={24} strokeWidth={1.6} className={form.tipo_lavoro === w.v ? "text-brand-orange" : "text-navy"} />
                            <p className="mt-2 text-sm font-semibold text-ink leading-snug">{w.v}</p>
                          </OptionCard>
                        );
                      })}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <h3 className="font-display font-bold text-xl text-navy mb-5">Quanto è grande il lavoro?</h3>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {SIZES.map((s) => (
                        <OptionCard key={s.v} active={form.dimensione === s.v} onClick={() => set("dimensione", s.v)} testid={`wiz-size-${s.v}`}>
                          <p className="font-display font-bold text-lg text-navy">{s.v}</p>
                          <p className="mt-1 text-sm text-ink/60">{s.d}</p>
                        </OptionCard>
                      ))}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <h3 className="font-display font-bold text-xl text-navy mb-5">Quando ti serve?</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {URGENCY.map((u) => {
                        const Icon = u.icon;
                        return (
                          <OptionCard key={u.v} active={form.urgenza === u.v} onClick={() => set("urgenza", u.v)} testid={`wiz-urgency-${u.v}`}>
                            <Icon size={22} className={form.urgenza === u.v ? "text-brand-orange" : "text-navy"} />
                            <p className="mt-2 text-sm font-semibold text-ink">{u.v}</p>
                          </OptionCard>
                        );
                      })}
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <h3 className="font-display font-bold text-xl text-navy mb-5">In che zona di Roma? *</h3>
                    <div className="relative">
                      <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40 z-10 pointer-events-none" />
                      <select
                        data-testid="wiz-zona"
                        className={`${fieldClass} pl-11 appearance-none cursor-pointer`}
                        value={form.zona}
                        onChange={(e) => set("zona", e.target.value)}
                      >
                        <option value="">Seleziona la zona</option>
                        {ROME_ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                      </select>
                    </div>
                    {form.zona === "Altra zona" && (
                      <input
                        data-testid="wiz-zona-altro"
                        className={`${fieldClass} mt-3`}
                        value={form.zona_altro}
                        onChange={(e) => set("zona_altro", e.target.value)}
                        placeholder="Specifica la zona o il quartiere *"
                      />
                    )}
                    <p className="mt-3 text-xs text-ink/45">Campo obbligatorio: ci aiuta a organizzare i sopralluoghi.</p>
                  </div>
                )}

                {step === 5 && (
                  <div>
                    <h3 className="font-display font-bold text-xl text-navy mb-2">Carica fotografie</h3>
                    <p className="text-sm text-ink/55 mb-4">Aiutano a darti una valutazione più precisa. Fino a {MAX_FILES} immagini.</p>
                    <div
                      onClick={() => inputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                      data-testid="wiz-upload-zone"
                      className="cursor-pointer rounded-2xl border-2 border-dashed border-gray-200 hover:border-brand-orange/50 hover:bg-surface transition-colors px-4 py-8 text-center"
                    >
                      <Upload size={26} className="mx-auto text-navy/50" />
                      <p className="mt-2 text-sm text-ink/60">Trascina qui o <span className="text-navy font-semibold">clicca per caricare</span></p>
                      <p className="text-xs text-ink/40 mt-1">{photos.length}/{MAX_FILES} · max 4MB ciascuna</p>
                      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="wiz-file-input" onChange={(e) => handleFiles(e.target.files)} />
                    </div>
                    {photos.length > 0 && (
                      <div className="mt-4 grid grid-cols-4 sm:grid-cols-5 gap-2">
                        {photos.map((p, i) => (
                          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                            <img src={p.data_url} alt={p.name} className="h-full w-full object-cover" />
                            <button type="button" onClick={() => setPhotos((x) => x.filter((_, idx) => idx !== i))} data-testid={`wiz-remove-${i}`} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center">
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {step === 6 && (
                  <div>
                    <h3 className="font-display font-bold text-xl text-navy mb-5">Descrivi il lavoro</h3>
                    <textarea
                      data-testid="wiz-descrizione"
                      rows={5}
                      className={`${fieldClass} resize-none`}
                      value={form.descrizione}
                      onChange={(e) => set("descrizione", e.target.value)}
                      placeholder="Raccontaci cosa ti serve: stato attuale, misure indicative, materiali..."
                    />
                  </div>
                )}

                {step === 7 && (
                  <div>
                    <h3 className="font-display font-bold text-xl text-navy mb-5">I tuoi contatti</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-1.5">Nome <span className="text-ink/40 font-normal">(facoltativo)</span></label>
                        <input data-testid="wiz-nome" className={fieldClass} value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Il tuo nome" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-1.5">{WHATSAPP_ENABLED ? "Telefono / WhatsApp *" : "Telefono *"}</label>
                        <input data-testid="wiz-telefono" className={`${fieldClass} ${errors.telefono ? "border-red-400 focus:border-red-400 focus:ring-red-100" : ""}`} value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="Es. 333 1234567" />
                        {errors.telefono && <p data-testid="wiz-error-telefono" className="mt-1.5 text-sm text-red-600">{errors.telefono}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-1.5">Email *</label>
                        <input data-testid="wiz-email" type="email" className={`${fieldClass} ${errors.email ? "border-red-400 focus:border-red-400 focus:ring-red-100" : ""}`} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="la-tua@email.it" />
                        {errors.email && <p data-testid="wiz-error-email" className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
                      </div>
                      <div>
                        <label className={`flex items-start gap-2.5 cursor-pointer text-sm ${errors.privacy ? "text-red-600" : "text-ink/70"}`}>
                          <input
                            type="checkbox"
                            data-testid="wiz-privacy"
                            checked={privacyConsent}
                            onChange={(e) => { setPrivacyConsent(e.target.checked); if (e.target.checked) setErrors((prev) => ({ ...prev, privacy: undefined })); }}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                          />
                          <span>
                            Ho letto e accetto la{" "}
                            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-navy underline underline-offset-2 hover:text-brand-orange">Privacy Policy</Link>
                            {" "}e autorizzo il trattamento dei miei dati per rispondere alla richiesta. *
                          </span>
                        </label>
                        {errors.privacy && <p data-testid="wiz-error-privacy" className="mt-1.5 text-sm text-red-600">{errors.privacy}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* nav */}
            <div className="mt-8 flex items-center justify-between gap-3">
              {step > 1 ? (
                <button type="button" onClick={back} data-testid="wiz-back" className="inline-flex items-center gap-1.5 rounded-full px-5 py-3 text-sm font-semibold text-ink/60 hover:text-navy transition-colors">
                  <ArrowLeft size={16} /> Indietro
                </button>
              ) : <span />}

              {step < TOTAL ? (
                <button type="button" onClick={next} data-testid="wiz-next" className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-[15px] font-semibold text-white hover:bg-navy-deep transition-colors">
                  Avanti <ArrowRight size={18} />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={loading} data-testid="wiz-submit" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 text-[15px] font-semibold text-white shadow-[0_10px_28px_-8px_rgba(242,140,40,0.8)] hover:bg-brand-orange-dark transition-colors disabled:opacity-70">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Invio...</> : <>Invia richiesta <CheckCircle2 size={18} /></>}
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 text-center">
            {WHATSAPP_ENABLED && (
              <>
                <p className="text-sm text-ink/50 mb-3">Preferisci scriverci direttamente?</p>
                <WhatsAppButton testid="wizard-whatsapp" label="Scrivici su WhatsApp" location="wizard" />
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
